import { useEffect, useState } from 'react';
import { SelectionTreeT } from 'business/definitions';
import { ResourceSoftwareIdField } from 'routes/BudgetModule/config/types';
import { LINE_CODE_FIELD } from 'routes/BudgetModule/config/constants';
import { translateBlockValue } from 'i18n';
import {
    EmptyProjectionDetails,
    ProjectionFiltersParams,
    ProjectionVentilation,
    VentilationsLabelMapping,
} from '../helpers/types';
import { useIntegrationSoftwares } from 'helpers/integrations';

type FiltersStates = {
    [ventilation: string]: SelectionTreeT[];
};

/**
 * Filters a list of projectionDetails according to current state of ventilations filters selected by the user.
 */
const filterProjectionDetails = (
    projectionDetailsWithProjections:
        | EmptyProjectionDetails.WithMetadata[]
        | undefined,
    filtersState: FiltersStates,
    code: string | undefined,
): EmptyProjectionDetails.WithMetadata[] | undefined => {
    let filteredProjectionsDetails = projectionDetailsWithProjections;
    Object.keys(filtersState).forEach((key) => {
        const values = filtersState[key];
        filteredProjectionsDetails = filteredProjectionsDetails?.filter(
            (detail) =>
                !values.length ||
                values
                    .map((res) => res.value)
                    .some((el) => el === detail[key as keyof typeof detail]),
        );
    });

    // SIFAC special case to manage
    if (code) {
        filteredProjectionsDetails = filteredProjectionsDetails?.filter(
            (detail) => detail[LINE_CODE_FIELD] === code,
        );
    }
    return filteredProjectionsDetails;
};

/**
 * A helper in charge of building the ProjectionFiltersParams objects.
 * It excludes ventilations not present in labelMappings.
 */
const buildProjectionFilters = (
    projectionDetailsWithProjections:
        | EmptyProjectionDetails.WithMetadata[]
        | undefined,
    filtersState: FiltersStates,
    setFiltersState: React.Dispatch<React.SetStateAction<FiltersStates>>,
    resourceSoftwareIdField: ResourceSoftwareIdField,
    projectionBySoftwareIdArray: ProjectionVentilation.SoftwareIds[],
    ventilationsLabelMapping: VentilationsLabelMapping | undefined,
    softwares: Set<string>,
): ProjectionFiltersParams => {
    if (Object.keys(filtersState).length === 0) return [];

    const ventilationExistsInLabelMappings = (
        el: ProjectionVentilation.SoftwareIds,
    ) => ventilationsLabelMapping && ventilationsLabelMapping[el];

    return [resourceSoftwareIdField, ...projectionBySoftwareIdArray]
        .filter(ventilationExistsInLabelMappings)
        .map((softwareIdField) => ({
            data: projectionDetailsWithProjections!,
            filtersState,
            setFiltersState,
            title: translateBlockValue(softwareIdField, true, softwares),
            labelField: ventilationsLabelMapping![softwareIdField],
        }));
};

type UseGetProjectionFiltersResponse = {
    filteredProjectionsDetails:
        | EmptyProjectionDetails.WithMetadata[]
        | undefined;
    filters: ProjectionFiltersParams;
};

/**
 * Build a filters states objects.
 */
const buildFiltersStates = (
    resourceSoftwareIdField: ResourceSoftwareIdField,
    projectionBySoftwareIdArray: ProjectionVentilation.SoftwareIds[],
    ventilationsLabelMapping: VentilationsLabelMapping,
): FiltersStates =>
    [resourceSoftwareIdField, ...projectionBySoftwareIdArray].reduce(
        (mapping, ventilation) => {
            const name = ventilationsLabelMapping[ventilation];
            return {
                ...mapping,
                [name]: [],
            };
        },
        {},
    );

/**
 * Custom hook to manage filters logic within the Pluriannual and Atterrissage Grids.
 * ProjectionsDetails are filtered based on user selections.
 * Filters options are constructed here, depending on available ventilations dimensions.
 * @returns ProjectionDetails filtered according to currently selected filters.
 * @returns Filters objects, aimed to be consumed by the ProjectionFilters.
 */
export const useGetProjectionFilters = (
    projectionDetailsWithProjections:
        | EmptyProjectionDetails.WithMetadata[]
        | undefined,
    isProjectionDetailsLoading: boolean,
    resourceSoftwareIdField: ResourceSoftwareIdField,
    projectionBySoftwareIdArray: ProjectionVentilation.SoftwareIds[],
    ventilationsLabelMapping: VentilationsLabelMapping | undefined,
    code: string | undefined,
): UseGetProjectionFiltersResponse => {
    // As filters are additives, we need to hold the state in this component
    const [filtersState, setFiltersState] = useState<FiltersStates>({});
    const { data: softwares } = useIntegrationSoftwares();

    useEffect(() => {
        if (!isProjectionDetailsLoading && ventilationsLabelMapping) {
            setFiltersState(
                buildFiltersStates(
                    resourceSoftwareIdField,
                    projectionBySoftwareIdArray,
                    ventilationsLabelMapping,
                ),
            );
        }
    }, [
        setFiltersState,
        isProjectionDetailsLoading,
        resourceSoftwareIdField,
        JSON.stringify(projectionBySoftwareIdArray),
        JSON.stringify(ventilationsLabelMapping),
    ]);

    if (softwares === undefined) {
        return { filteredProjectionsDetails: [], filters: [] };
    }

    const filters = buildProjectionFilters(
        projectionDetailsWithProjections,
        filtersState,
        setFiltersState,
        resourceSoftwareIdField,
        projectionBySoftwareIdArray,
        ventilationsLabelMapping,
        softwares,
    );

    const filteredProjectionsDetails = filterProjectionDetails(
        projectionDetailsWithProjections,
        filtersState,
        code,
    );

    return { filteredProjectionsDetails, filters };
};
